import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  IconButton,
  Stack,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material'
import {
  Settings as SettingsIcon,
  AttachMoney as PricingIcon,
  Storage as StorageIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  StarBorder as StarBorderIcon,
  MailOutline as MailOutlineIcon,
  AccessTime as AccessTimeIcon,
  NotificationsActive as NotificationsActiveIcon,
  MoneyOff as PenaltyIcon,
} from '@mui/icons-material'
import { configService } from '../api/adminServices'
import { ResizableTableCell } from '../components/ResizableTable'
import { useToast } from '../components/useToast'
import { describeCronExpression, looksLikeCronExpression } from '../utils/cronHumanize'
import CronBuilder from '../components/CronBuilder'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

// Configuration field types
interface ConfigField {
  key: string
  label: string
  type: 'string' | 'number' | 'boolean' | 'currency' | 'percentage' | 'cron'
  description?: string
  category: string
  editable?: boolean
}

// Helper function to dynamically extract scalar fields from one level of the
// config object. Object/array values are NOT included — those are rendered as
// nested sub-sections instead so deep configs (e.g. matching → cron → schedule)
// stay readable.
const extractConfigFields = (config: any, category: string): ConfigField[] => {
  if (!config || typeof config !== 'object') return []

  return Object.keys(config)
    .filter((key) => {
      const v = config[key]
      // Treat plain objects as nested sections, not scalar fields.
      return !(v !== null && typeof v === 'object' && !Array.isArray(v))
    })
    .map(key => {
      const value = config[key]
      let type: 'string' | 'number' | 'boolean' | 'currency' | 'percentage' | 'cron' = 'string'

      if (typeof value === 'boolean') {
        type = 'boolean'
      } else if (typeof value === 'number') {
        // Check if it's a percentage field
        if (key.toLowerCase().includes('percent') || key.toLowerCase().includes('rate') || key.toLowerCase().includes('surcharge')) {
          type = 'percentage'
        } else if (key.toLowerCase().includes('price') || key.toLowerCase().includes('cost') || key.toLowerCase().includes('rate') || key.toLowerCase().includes('fee')) {
          type = 'currency'
        } else {
          type = 'number'
        }
      } else if (
        looksLikeCronExpression(value) ||
        /cron|schedule/i.test(key)
      ) {
        type = 'cron'
      }

      return {
        key,
        label: prettifyKey(key),
        type,
        category,
        editable: true,
      }
    })
}

/** "onTimeDeliveryMax" → "On Time Delivery Max" (single source of truth). */
const prettifyKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

const getValueAtPath = (obj: any, path: string[]): any => {
  let cur: any = obj
  for (const k of path) {
    if (cur == null) return undefined
    cur = cur[k]
  }
  return cur
}

const setValueAtPath = (obj: any, path: string[], value: any): any => {
  if (path.length === 0) return value
  const [head, ...rest] = path
  const base = isPlainObject(obj) ? { ...obj } : {}
  base[head] = setValueAtPath(isPlainObject(base[head]) ? base[head] : {}, rest, value)
  return base
}

/** Flatten a nested object into { "a.b.c": value } so the diff view can
 *  enumerate every changed scalar across sub-sections. */
const flattenScalars = (
  obj: any,
  prefix: string[] = [],
): Record<string, any> => {
  const out: Record<string, any> = {}
  if (!isPlainObject(obj)) {
    if (prefix.length > 0) out[prefix.join('.')] = obj
    return out
  }
  for (const [k, v] of Object.entries(obj)) {
    if (isPlainObject(v)) {
      Object.assign(out, flattenScalars(v, [...prefix, k]))
    } else {
      out[[...prefix, k].join('.')] = v
    }
  }
  return out
}

export default function ConfigPage() {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [activeTab, setActiveTab] = useState(0)
  const [editMode, setEditMode] = useState(false)
  const [editedConfigs, setEditedConfigs] = useState<Record<string, Record<string, any>>>({})
  const [diffOpen, setDiffOpen] = useState(false)

  // Fetch pricing configuration
  const { data: pricingConfig, isLoading: pricingLoading, refetch: refetchPricing } = useQuery({
    queryKey: ['pricing-config'],
    queryFn: () => configService.getPricingConfig(),
  })

  // Fetch matching configuration
  const { data: matchingConfig, isLoading: matchingLoading, refetch: refetchMatching } = useQuery({
    queryKey: ['matching-config'],
    queryFn: () => configService.getMatchingConfig(),
  })

  // Fetch routing configuration
  const { data: routingConfig, isLoading: routingLoading, refetch: refetchRouting } = useQuery({
    queryKey: ['routing-config'],
    queryFn: () => configService.getRoutingConfig(),
  })

  // Fetch scoring configuration
  const { data: scoringConfig, isLoading: scoringLoading, refetch: refetchScoring } = useQuery({
    queryKey: ['scoring-config'],
    queryFn: () => configService.getScoringConfig(),
  })

  // Fetch receiver-confirmation configuration
  const {
    data: receiverConfirmationConfig,
    isLoading: receiverConfirmationLoading,
    refetch: refetchReceiverConfirmation,
  } = useQuery({
    queryKey: ['receiver-confirmation-config'],
    queryFn: () => configService.getReceiverConfirmationConfig(),
  })

  // Fetch market-hours configuration
  const {
    data: marketHoursConfig,
    isLoading: marketHoursLoading,
    refetch: refetchMarketHours,
  } = useQuery({
    queryKey: ['market-hours-config'],
    queryFn: () => configService.getMarketHoursConfig(),
  })

  // Fetch driver-reminder configuration
  const {
    data: driverReminderConfig,
    isLoading: driverReminderLoading,
    refetch: refetchDriverReminder,
  } = useQuery({
    queryKey: ['driver-reminder-config'],
    queryFn: () => configService.getDriverReminderConfig(),
  })

  // Fetch penalty configuration — the focused, validated view of matching.cancellationPenalty + matching.penalty
  const {
    data: penaltyConfig,
    isLoading: penaltyLoading,
    refetch: refetchPenalty,
  } = useQuery({
    queryKey: ['penalty-config'],
    queryFn: () => configService.getPenaltyConfig(),
  })

  // Get cache statistics
  const { data: cacheStats, refetch: refetchCacheStats } = useQuery({
    queryKey: ['cache-stats'],
    queryFn: () => configService.getCacheStats(),
  })

  const isLoading =
    pricingLoading ||
    matchingLoading ||
    routingLoading ||
    scoringLoading ||
    receiverConfirmationLoading ||
    marketHoursLoading ||
    driverReminderLoading ||
    penaltyLoading

  const getCategoryId = (tabIndex: number): string => {
    switch (tabIndex) {
      case 0:
        return 'pricing'
      case 1:
        return 'matching'
      case 2:
        return 'routing'
      case 3:
        return 'scoring'
      case 4:
        return 'receiverConfirmation'
      case 5:
        return 'marketHours'
      case 6:
        return 'driverReminder'
      case 7:
        return 'penalty'
      default:
        return 'pricing'
    }
  }

  /** Map category ids to React Query keys (camelCase sections use kebab-case keys). */
  const queryKeyForCategory = (category: string): string => {
    switch (category) {
      case 'receiverConfirmation':
        return 'receiver-confirmation-config'
      case 'marketHours':
        return 'market-hours-config'
      case 'driverReminder':
        return 'driver-reminder-config'
      default:
        return `${category}-config`
    }
  }

  // Each query response is { section, source, editable, persisted, values }; the
  // current/edited config maps and the renderer all work off the `values` map.
  const sectionValues = (section: string): Record<string, any> => {
    switch (section) {
      case 'pricing':
        return pricingConfig?.values ?? {}
      case 'matching':
        return matchingConfig?.values ?? {}
      case 'routing':
        return routingConfig?.values ?? {}
      case 'scoring':
        return scoringConfig?.values ?? {}
      case 'receiverConfirmation':
        return receiverConfirmationConfig?.values ?? {}
      case 'marketHours':
        return marketHoursConfig?.values ?? {}
      case 'driverReminder':
        return driverReminderConfig?.values ?? {}
      case 'penalty':
        return penaltyConfig?.values ?? {}
      default:
        return {}
    }
  }

  const sectionMeta = (section: string) => {
    switch (section) {
      case 'pricing':
        return pricingConfig
      case 'matching':
        return matchingConfig
      case 'routing':
        return routingConfig
      case 'scoring':
        return scoringConfig
      case 'receiverConfirmation':
        return receiverConfirmationConfig
      case 'marketHours':
        return marketHoursConfig
      case 'driverReminder':
        return driverReminderConfig
      case 'penalty':
        return penaltyConfig
      default:
        return undefined
    }
  }

  const getCurrentConfig = (): Record<string, any> => {
    const category = getCategoryId(activeTab)
    const original = sectionValues(category)
    return editMode ? editedConfigs[category] ?? original : original
  }

  const currentConfig = getCurrentConfig() || {}
  const currentMeta = sectionMeta(getCategoryId(activeTab))

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({ category, config }: { category: string; config: Record<string, any> }) => {
      switch (category) {
        case 'pricing':
          return await configService.updatePricingConfig(config)
        case 'matching':
          return await configService.updateMatchingConfig(config)
        case 'routing':
          return await configService.updateRoutingConfig(config)
        case 'scoring':
          return await configService.updateScoringConfig(config)
        case 'receiverConfirmation':
          return await configService.updateReceiverConfirmationConfig(config)
        case 'marketHours':
          return await configService.updateMarketHoursConfig(config)
        case 'driverReminder':
          return await configService.updateDriverReminderConfig(config)
        case 'penalty':
          // Validated server-side against the merged result (PenaltyConfigValidator): an incoherent ladder or
          // a compensation share outside 0–100 is rejected with a 400 naming the key, and nothing is applied.
          return await configService.updatePenaltyConfig(config)
        default:
          throw new Error('Invalid category')
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [queryKeyForCategory(variables.category)] })
      // Penalties and matching are two views of the same stored blocks.
      if (variables.category === 'penalty') queryClient.invalidateQueries({ queryKey: ['matching-config'] })
      if (variables.category === 'matching') queryClient.invalidateQueries({ queryKey: ['penalty-config'] })
      toast.success('Configuration updated successfully!')
      setEditMode(false)
      setEditedConfigs({})
    },
    onError: (error) => toast.errorFrom(error, 'Failed to update configuration'),
  })

  // Seed each section's edit buffer with the latest values whenever the
  // server response refreshes (so cancel resets to current truth).
  useEffect(() => {
    if (pricingConfig) setEditedConfigs((p) => ({ ...p, pricing: { ...pricingConfig.values } }))
  }, [pricingConfig])
  useEffect(() => {
    if (matchingConfig) setEditedConfigs((p) => ({ ...p, matching: { ...matchingConfig.values } }))
  }, [matchingConfig])
  useEffect(() => {
    if (routingConfig) setEditedConfigs((p) => ({ ...p, routing: { ...routingConfig.values } }))
  }, [routingConfig])
  useEffect(() => {
    if (scoringConfig) setEditedConfigs((p) => ({ ...p, scoring: { ...scoringConfig.values } }))
  }, [scoringConfig])
  useEffect(() => {
    if (receiverConfirmationConfig)
      setEditedConfigs((p) => ({
        ...p,
        receiverConfirmation: { ...receiverConfirmationConfig.values },
      }))
  }, [receiverConfirmationConfig])
  useEffect(() => {
    if (marketHoursConfig)
      setEditedConfigs((p) => ({ ...p, marketHours: { ...marketHoursConfig.values } }))
  }, [marketHoursConfig])
  useEffect(() => {
    if (driverReminderConfig)
      setEditedConfigs((p) => ({ ...p, driverReminder: { ...driverReminderConfig.values } }))
  }, [driverReminderConfig])
  useEffect(() => {
    if (penaltyConfig) setEditedConfigs((p) => ({ ...p, penalty: { ...penaltyConfig.values } }))
  }, [penaltyConfig])

  const handleEditToggle = () => {
    if (editMode) {
      // Cancel - reset to original
      const category = getCategoryId(activeTab)
      setEditedConfigs(prev => {
        const newConfigs = { ...prev }
        delete newConfigs[category]
        return newConfigs
      })
    }
    setEditMode(!editMode)
  }

  const handleOpenDiff = () => setDiffOpen(true)
  const handleConfirmSave = () => {
    const category = getCategoryId(activeTab)
    const configToSave = editedConfigs[category] || currentConfig
    updateConfigMutation.mutate({ category, config: configToSave })
    setDiffOpen(false)
  }

  /** Compute changed scalar paths between original (server) and edited config.
   *  Recurses into nested sub-sections so e.g. "matching.cron.schedule" shows up. */
  const computeDiff = () => {
    const category = getCategoryId(activeTab)
    const original = flattenScalars(sectionValues(category))
    const edited = flattenScalars(editedConfigs[category] ?? {})

    const keys = new Set<string>([...Object.keys(original), ...Object.keys(edited)])
    type DiffRow = {
      key: string
      before: any
      after: any
      kind: 'changed' | 'added' | 'removed'
    }
    const rows: DiffRow[] = []
    keys.forEach((k) => {
      const before = original[k]
      const after = edited[k]
      if (JSON.stringify(before) === JSON.stringify(after)) return
      if (before === undefined) rows.push({ key: k, before, after, kind: 'added' })
      else if (after === undefined) rows.push({ key: k, before, after, kind: 'removed' })
      else rows.push({ key: k, before, after, kind: 'changed' })
    })
    rows.sort((a, b) => a.key.localeCompare(b.key))
    return rows
  }

  const handleFieldChange = (path: string[], value: any) => {
    const category = getCategoryId(activeTab)
    setEditedConfigs(prev => {
      const base = prev[category] ?? sectionValues(category)
      return { ...prev, [category]: setValueAtPath(base, path, value) }
    })
  }

  const categories = [
    { id: 'pricing', label: 'Pricing', icon: <PricingIcon /> },
    { id: 'matching', label: 'Matching', icon: <SettingsIcon /> },
    { id: 'routing', label: 'Routing', icon: <StorageIcon /> },
    { id: 'scoring', label: 'Scoring', icon: <StarBorderIcon /> },
    { id: 'receiverConfirmation', label: 'Receiver Confirmation', icon: <MailOutlineIcon /> },
    { id: 'marketHours', label: 'Market Hours', icon: <AccessTimeIcon /> },
    { id: 'driverReminder', label: 'Driver Reminder', icon: <NotificationsActiveIcon /> },
    { id: 'penalty', label: 'Penalties', icon: <PenaltyIcon /> },
  ]

  const renderConfigField = (field: ConfigField, path: string[], value: any) => {
    const category = getCategoryId(activeTab)
    const edited = editedConfigs[category]
    const editedValue = edited !== undefined ? getValueAtPath(edited, path) : undefined
    const currentValue = editMode && editedValue !== undefined ? editedValue : value

    if (field.type === 'boolean') {
      return (
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(currentValue)}
              onChange={(e) => handleFieldChange(path, e.target.checked)}
              disabled={!editMode}
              color="primary"
            />
          }
          label={currentValue ? 'Enabled' : 'Disabled'}
        />
      )
    }

    if (field.type === 'currency') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => handleFieldChange(path, parseFloat(e.target.value) || 0)}
          disabled={!editMode}
          InputProps={{
            startAdornment: <InputAdornment position="start">₪</InputAdornment>,
          }}
          inputProps={{ step: 0.01, min: 0 }}
        />
      )
    }

    if (field.type === 'percentage') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => handleFieldChange(path, parseFloat(e.target.value) || 0)}
          disabled={!editMode}
          InputProps={{
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
          inputProps={{ step: 0.1, min: 0, max: 100 }}
        />
      )
    }

    if (field.type === 'number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          value={currentValue ?? ''}
          onChange={(e) => handleFieldChange(path, parseInt(e.target.value) || 0)}
          disabled={!editMode}
          inputProps={{ min: 0 }}
        />
      )
    }

    if (field.type === 'cron') {
      const expr = String(currentValue ?? '')
      const description = describeCronExpression(expr)
      const valid = !expr || looksLikeCronExpression(expr)
      if (editMode) {
        return (
          <CronBuilder
            value={expr}
            onChange={(next) => handleFieldChange(path, next)}
          />
        )
      }
      return (
        <Stack spacing={0.25}>
          <TextField
            fullWidth
            size="small"
            value={expr}
            disabled
            error={!valid}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
          {expr && (
            <Typography
              variant="caption"
              color={description ? 'text.secondary' : 'warning.main'}
              sx={{ pl: 0.5 }}
            >
              {description ?? `Cron: ${expr}`}
            </Typography>
          )}
        </Stack>
      )
    }

    return (
      <TextField
        fullWidth
        size="small"
        value={currentValue ?? ''}
        onChange={(e) => handleFieldChange(path, e.target.value)}
        disabled={!editMode}
      />
    )
  }

  const renderScalarTable = (
    fields: ConfigField[],
    pathPrefix: string[],
    data: any,
  ) => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <ResizableTableCell width="30%">Setting</ResizableTableCell>
            <ResizableTableCell width="70%">Value</ResizableTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((field) => {
            const path = [...pathPrefix, field.key]
            return (
              <TableRow key={field.key} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {field.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {path.join('.')}
                  </Typography>
                </TableCell>
                <TableCell>
                  {renderConfigField(field, path, data?.[field.key])}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )

  /** Recursively render a config object: scalars in a table, plain objects as
   *  nested sub-sections (heading + indented body). Arrays are read-only JSON. */
  const renderSection = (
    categoryId: string,
    data: any,
    pathPrefix: string[],
    breadcrumbs: string[],
    depth = 0,
  ): React.ReactNode => {
    if (!isPlainObject(data)) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No configuration data available
          </Typography>
        </Box>
      )
    }

    const scalarFields = extractConfigFields(data, categoryId)
    const nestedEntries = Object.entries(data).filter(([, v]) => isPlainObject(v))

    return (
      <Stack spacing={2}>
        {scalarFields.length > 0 && renderScalarTable(scalarFields, pathPrefix, data)}
        {nestedEntries.map(([key, value]) => {
          const childBreadcrumbs = [...breadcrumbs, prettifyKey(key)]
          return (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: depth % 2 === 0
                  ? alpha(theme.palette.primary.main, 0.02)
                  : alpha(theme.palette.action.hover, 0.4),
                borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.4)}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1.5 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ color: 'primary.main' }}
                >
                  {childBreadcrumbs.join(' → ')}
                </Typography>
                <Chip
                  label={[...pathPrefix, key].join('.')}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                />
              </Stack>
              {renderSection(
                categoryId,
                value,
                [...pathPrefix, key],
                childBreadcrumbs,
                depth + 1,
              )}
            </Paper>
          )
        })}
      </Stack>
    )
  }

  const renderCategoryContent = (categoryId: string) => {
    const configData = getCurrentConfig()
    return renderSection(categoryId, configData, [], [])
  }

  if (isLoading) {
    return (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box className="animate-fade-in" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh configuration cache">
            <IconButton
              onClick={async () => {
                try {
                  await configService.refreshCache()
                  refetchPricing()
                  refetchMatching()
                  refetchRouting()
                  refetchScoring()
                  refetchReceiverConfirmation()
                  refetchMarketHours()
                  refetchDriverReminder()
                  refetchPenalty()
                  refetchCacheStats()
                  toast.success('Configuration cache refreshed!')
                } catch (error) {
                  toast.errorFrom(error, 'Failed to refresh cache')
                }
              }}
              disabled={editMode}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {editMode ? (
            <>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CancelIcon />}
                onClick={handleEditToggle}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleOpenDiff}
                disabled={updateConfigMutation.isPending}
              >
                {updateConfigMutation.isPending ? 'Saving...' : 'Review & Save'}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditToggle}
            >
              Edit Configuration
            </Button>
          )}
        </Box>
      </Box>

      {/* Source / persistence banner */}
      {currentMeta && (
        <Alert
          severity={currentMeta.persisted ? 'success' : 'warning'}
          sx={{ mb: 2 }}
        >
          Source: <strong>{currentMeta.source}</strong> ·{' '}
          {currentMeta.persisted
            ? 'Changes will be persisted to AWS AppConfig.'
            : 'AWS AppConfig is disabled in this profile — changes apply to the running service in-memory and revert on restart.'}
        </Alert>
      )}

      {/* Edit Mode Banner */}
      {editMode && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          icon={<EditIcon />}
        >
          You are in edit mode. Make your changes and click "Save Changes" to apply them.
        </Alert>
      )}

      {/* Configuration Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              if (editMode) {
                // Reset edited configs when switching tabs
                const oldCategory = getCategoryId(activeTab)
                setEditedConfigs(prev => {
                  const newConfigs = { ...prev }
                  delete newConfigs[oldCategory]
                  return newConfigs
                })
                setEditMode(false)
              }
              setActiveTab(newValue)
            }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {categories.map((cat) => (
              <Tab
                key={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cat.icon}
                    {cat.label}
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        <CardContent>
          {categories.map((cat, index) => (
            <TabPanel key={cat.id} value={activeTab} index={index}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {cat.icon}
                  {cat.label} Settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configure {cat.label.toLowerCase()} related parameters for the platform.
                </Typography>
              </Box>
              <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                {renderCategoryContent(cat.id)}
              </Paper>
            </TabPanel>
          ))}
        </CardContent>
      </Card>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: alpha(theme.palette.success.main, 0.05),
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <CheckIcon sx={{ fontSize: 40, color: 'success.main' }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                System Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                All services operational
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <StorageIcon sx={{ fontSize: 40, color: 'info.main' }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Configuration Loaded
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Object.keys(currentConfig || {}).length} settings in current category
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.warning.main, 0.05),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" mb={1}>
              <InfoIcon sx={{ fontSize: 40, color: 'warning.main' }} />
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  Cache Statistics
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cacheStats
                    ? `${Object.keys(cacheStats).length} entries reported by AppConfig`
                    : 'Loading…'}
                </Typography>
              </Box>
              <Tooltip title="Refresh stats">
                <IconButton size="small" onClick={() => refetchCacheStats()}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            {cacheStats && Object.keys(cacheStats).length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {Object.entries(cacheStats)
                  .slice(0, 6)
                  .map(([k, v]) => (
                    <Stack
                      key={k}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: 'monospace' }}
                        color="text.secondary"
                      >
                        {k}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={typeof v === 'object' ? JSON.stringify(v) : String(v)}
                        sx={{ maxWidth: 180 }}
                      />
                    </Stack>
                  ))}
                {Object.keys(cacheStats).length > 6 && (
                  <Typography variant="caption" color="text.secondary">
                    +{Object.keys(cacheStats).length - 6} more entries
                  </Typography>
                )}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Diff preview dialog */}
      <Dialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Review Changes</DialogTitle>
        <DialogContent dividers>
          {(() => {
            const diff = computeDiff()
            if (diff.length === 0) {
              return (
                <Alert severity="info">
                  No changes detected — the edited values match the server.
                </Alert>
              )
            }
            return (
              <Stack spacing={1}>
                <Alert severity="warning">
                  {diff.length} field{diff.length > 1 ? 's' : ''} will be updated.
                  Review carefully — these settings affect platform behaviour immediately.
                </Alert>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field</TableCell>
                        <TableCell>Before</TableCell>
                        <TableCell>After</TableCell>
                        <TableCell width={90}>Kind</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {diff.map((row) => (
                        <TableRow key={row.key} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {row.key}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              color: 'text.secondary',
                              textDecoration: row.kind === 'removed' ? 'line-through' : 'none',
                            }}
                          >
                            {row.before === undefined
                              ? '—'
                              : typeof row.before === 'object'
                              ? JSON.stringify(row.before)
                              : String(row.before)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                            }}
                          >
                            {row.after === undefined
                              ? '—'
                              : typeof row.after === 'object'
                              ? JSON.stringify(row.after)
                              : String(row.after)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.kind}
                              size="small"
                              color={
                                row.kind === 'added'
                                  ? 'success'
                                  : row.kind === 'removed'
                                  ? 'error'
                                  : 'warning'
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiffOpen(false)}>Back to Edit</Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleConfirmSave}
            disabled={updateConfigMutation.isPending || computeDiff().length === 0}
          >
            {updateConfigMutation.isPending ? 'Saving…' : 'Confirm & Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
